import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Row,
  Typography,
  Space,
  Spin,
  message,
  Avatar,
  Card,
  Empty,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  MinusOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { helper } from "@src/controls/controlHelper";
import dayjs from "dayjs";
import queryString from "qs";

const { Text, Title } = Typography;

interface TicketDetailCtrlProps {
  eventId: number;
  pageInfo: any;
  onBack: () => void;
}

const TicketDetailCtrl: React.FC<TicketDetailCtrlProps> = ({
  eventId,
  pageInfo,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [cart, setCart] = useState<Record<number, number>>({});
  const [step, setStep] = useState<"cart" | "checkout">("cart");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "online">("cash");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [paymentViewTitle, setPaymentViewTitle] = useState("Thanh toán");
  // const [isShowPrint, setIsShowPrint] = useState(false);
  const paymentViewRef = useRef<HTMLDivElement | null>(null);
  const paymentObjectUrlRef = useRef<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const rs: any = await helper.callPageApi(pageInfo, "get-ticket-info", {
        id: eventId,
      });
      // Handle the nested data structure matching the typical CMS API response
      const payload = rs?.data?.data || rs?.data || {};
      setData(payload);
    } catch (error) {
      console.error(error);
      message.error("Lỗi khi tải thông tin chi tiết sự kiện");
    } finally {
      setLoading(false);
    }
  }, [eventId, pageInfo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (paymentObjectUrlRef.current) {
        URL.revokeObjectURL(paymentObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!paymentUrl) return;

    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      paymentViewRef.current?.scrollIntoView({ block: "start" });

      let parent = paymentViewRef.current?.parentElement;
      while (parent) {
        parent.scrollTop = 0;
        parent.scrollLeft = 0;
        parent = parent.parentElement;
      }

      document
        .querySelectorAll<HTMLElement>(
          [
            "#root",
            ".gx-app-layout",
            ".gx-layout-content",
            ".gx-main-content-wrapper",
            ".ant-layout",
            ".ant-layout-content",
          ].join(",")
        )
        .forEach((element) => {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        });

      document.querySelectorAll<HTMLElement>("*").forEach((element) => {
        if (element.scrollTop > 0 || element.scrollLeft > 0) {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        }
      });
    };

    resetScroll();
    const frameId = window.requestAnimationFrame(resetScroll);
    const timeoutId = window.setTimeout(resetScroll, 0);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [paymentUrl]);

  const handleIncrease = (ticket: any) => {
    const current = cart[ticket.id] || 0;
    const maxItems = ticket.maxItemsPerOrder || 10;
    const max = Math.min(ticket.availableQuantity, maxItems);

    if (current >= max) {
      message.warning(`Bạn chỉ được mua tối đa ${max} vé loại này!`);
      return;
    }
    setCart({ ...cart, [ticket.id]: current + 1 });
  };

  const handleDecrease = (ticket: any) => {
    const current = cart[ticket.id] || 0;
    if (current <= 0) return;

    const newCart = { ...cart };
    if (current === 1) {
      delete newCart[ticket.id];
    } else {
      newCart[ticket.id] = current - 1;
    }
    setCart(newCart);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data || !data.id) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Quay lại
        </Button>
        <Empty description="Không tìm thấy sự kiện" style={{ marginTop: 40 }} />
      </div>
    );
  }

  const tickets = data.ticket || [];
  const totalAmount = Object.entries(cart).reduce((sum, [id, qty]) => {
    const ticket = tickets.find((t: any) => t.id === Number(id));
    return sum + (ticket?.price || 0) * qty;
  }, 0);
  const totalQty = Object.values(cart).reduce((sum, qty) => sum + qty, 0);

  const openEmbeddedUrl = (url: string, title: string, isObjectUrl = false) => {
    if (paymentObjectUrlRef.current) {
      URL.revokeObjectURL(paymentObjectUrlRef.current);
      paymentObjectUrlRef.current = null;
    }

    if (isObjectUrl) {
      paymentObjectUrlRef.current = url;
    }

    setPaymentViewTitle(title);
    setPaymentUrl(url);
  };

  const getBuyTicketRequestConfig = (responseType?: "blob" | "json") => {
    const api: any = helper.getApiByName(pageInfo.apis, "buy-ticket");
    let url: string = api.url;

    if (url.startsWith("/api/")) {
      url = url.substr(4, url.length - 1);
    }

    if (api.method === "GET") {
      url += `?${queryString.stringify({
        page: pageInfo.id,
        api: api.name,
      })}`;
    } else {
      url += `?${queryString.stringify({ page: pageInfo.id, api: api.name })}`;
    }

    const token = localStorage.getItem("token");

    return {
      url,
      method: api.method,
      responseType,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept:
          responseType === "blob" ? "application/pdf" : "application/json",
        "Content-Type": "application/json",
      },
    };
  };

  const parseErrorMessage = async (data: any) => {
    if (data?.message) return data.message;

    if (!(data instanceof Blob)) {
      return "Tạo đơn hàng thất bại";
    }

    try {
      const text = await data.text();
      const parsedData = JSON.parse(text);
      return parsedData?.message || "Tạo đơn hàng thất bại";
    } catch (error) {
      return "Tạo đơn hàng thất bại";
    }
  };

  if (paymentUrl) {
    return (
      <div
        ref={paymentViewRef}
        style={{
          backgroundColor: "#fff",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "16px 24px",
            borderBottom: "1px solid #e8e8e8",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space size={16} align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setPaymentUrl(null)}
              size="large"
              style={{ padding: "0 8px 0 0" }}
            />
            <Title level={4} style={{ margin: 0 }}>
              {paymentViewTitle}
            </Title>
          </Space>

          {/* {isShowPrint && (
            <Button type="primary" onClick={() => setIsShowPrint(false)}>
              In vé
            </Button>
          )} */}
        </div>
        <iframe
          title="Thanh toán vé"
          src={paymentUrl}
          style={{
            border: 0,
            flex: 1,
            minHeight: "calc(100vh - 73px)",
            width: "100%",
          }}
        />
      </div>
    );
  }

  const renderTicket = (ticket: any) => {
    const isSoldOut = ticket.availableQuantity <= 0;
    const qtyInCart = cart[ticket.id] || 0;

    return (
      <Row
        key={ticket.id}
        align="middle"
        style={{ padding: "16px 0", borderBottom: "1px solid #f0f0f0" }}
        wrap={false}
      >
        <Col>
          <Avatar
            size={56}
            src={ticket.image}
            style={{ backgroundColor: "#f5f5f5", border: "1px solid #f0f0f0" }}
          />
        </Col>
        <Col flex="auto" style={{ padding: "0 16px", minWidth: 0 }}>
          <Text
            strong
            style={{
              fontSize: 16,
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ticket.name}
          </Text>
          <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
            {ticket.price.toLocaleString("vi-VN")}đ
            {ticket.originPrice > ticket.price && (
              <Text delete style={{ marginLeft: 8 }}>
                {ticket.originPrice.toLocaleString("vi-VN")}đ
              </Text>
            )}
          </Text>
        </Col>
        <Col>
          {isSoldOut ? (
            <Button disabled style={{ borderRadius: 6 }}>
              Đã bán hết
            </Button>
          ) : qtyInCart > 0 ? (
            <Space size={12}>
              <Button
                shape="circle"
                icon={<MinusOutlined />}
                onClick={() => handleDecrease(ticket)}
              />
              <Text
                strong
                style={{
                  fontSize: 16,
                  minWidth: 20,
                  textAlign: "center",
                  display: "inline-block",
                }}
              >
                {qtyInCart}
              </Text>
              <Button
                shape="circle"
                icon={<PlusOutlined />}
                onClick={() => handleIncrease(ticket)}
              />
            </Space>
          ) : (
            <Button
              type="text"
              icon={
                <ShoppingCartOutlined
                  style={{ fontSize: 24, color: "#025433" }}
                />
              }
              onClick={() => handleIncrease(ticket)}
            />
          )}
        </Col>
      </Row>
    );
  };

  if (step === "checkout") {
    return (
      <div
        style={{
          backgroundColor: "#f5f5f5",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "#fff",
            padding: "16px 24px",
            borderBottom: "1px solid #e8e8e8",
          }}
        >
          <Space size={16} align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep("cart")}
              size="large"
              style={{ padding: "0 8px 0 0" }}
            />
            <Title level={4} style={{ margin: 0 }}>
              Chi tiết đơn hàng
            </Title>
          </Space>
        </div>

        {/* Main Content */}
        <div style={{ padding: "24px", flex: 1, paddingBottom: 100 }}>
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} lg={16} xl={14}>
              <Card
                bordered={false}
                bodyStyle={{ padding: "24px" }}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Title level={5} style={{ marginBottom: 16 }}>
                  Tóm tắt đơn hàng
                </Title>
                {Object.entries(cart).map(([id, qty]) => {
                  if (qty <= 0) return null;
                  const ticket = tickets.find((t: any) => t.id === Number(id));
                  if (!ticket) return null;
                  return (
                    <Row
                      key={id}
                      justify="space-between"
                      style={{ marginBottom: 8 }}
                    >
                      <Col>
                        <Text>
                          {ticket.name} x {qty}
                        </Text>
                      </Col>
                      <Col>
                        <Text strong>
                          {((ticket.price || 0) * qty).toLocaleString("vi-VN")}đ
                        </Text>
                      </Col>
                    </Row>
                  );
                })}
                <Divider style={{ margin: "16px 0" }} />
                <Row justify="space-between" align="middle">
                  <Col>
                    <Title level={5} style={{ margin: 0 }}>
                      Tổng tiền
                    </Title>
                  </Col>
                  <Col>
                    <Title level={4} style={{ color: "#e2111c", margin: 0 }}>
                      {totalAmount.toLocaleString("vi-VN")}đ
                    </Title>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Footer */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#fff",
            padding: "16px 24px",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
            borderTop: "1px solid #f0f0f0",
            zIndex: 10,
          }}
        >
          <Row justify="center">
            <Col xs={24} lg={16} xl={14}>
              <Button
                type="primary"
                size="large"
                block
                loading={isSubmitting}
                style={{
                  backgroundColor: "#025433",
                  borderColor: "#025433",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
                onClick={async () => {
                  setIsSubmitting(true);
                  const items = Object.entries(cart).map(([id, quantity]) => ({
                    ticketId: Number(id),
                    quantity,
                  }));

                  const payload = {
                    eventId: data.id,
                    salesChannel: "ONLINE",
                    items,
                    paymentMethod,
                    phone: phoneNumber,
                  };

                  try {
                    if (paymentMethod === "cash") {
                      const rs = await axios({
                        ...getBuyTicketRequestConfig("blob"),
                        data: payload,
                      });

                      const contentType = (
                        rs.headers?.["content-type"] || ""
                      ).toLowerCase();
                      const responseBlob =
                        rs.data instanceof Blob
                          ? rs.data
                          : new Blob([rs.data], { type: "application/pdf" });

                      if (
                        contentType.includes("application/pdf") ||
                        responseBlob.type.includes("application/pdf")
                      ) {
                        const pdfBlob = responseBlob.type
                          ? responseBlob
                          : new Blob([responseBlob], {
                              type: "application/pdf",
                            });

                        message.success("Tạo đơn hàng thành công!");
                        setCart({});
                        setStep("cart");
                        setPhoneNumber("");
                        setPaymentMethod("cash");

                        // @ts-ignore
                        if (
                          window.SunmiPrinter &&
                          typeof window.SunmiPrinter.printPDF === "function"
                        ) {
                          const reader = new FileReader();
                          reader.readAsDataURL(pdfBlob);
                          reader.onloadend = () => {
                            const base64data = reader.result as string;
                            // @ts-ignore
                            window.SunmiPrinter.printPDF(base64data);
                          };
                        } else {
                          const pdfUrl = URL.createObjectURL(pdfBlob);
                          openEmbeddedUrl(pdfUrl, "Quay lại", true);
                        }
                        return;
                      }

                      message.error(await parseErrorMessage(responseBlob));
                      return;
                    }

                    const rs = await axios({
                      ...getBuyTicketRequestConfig("json"),
                      data: payload,
                    });

                    const responseData = rs?.data || rs;

                    if (responseData && responseData.code === 0) {
                      message.success(
                        responseData.message || "Tạo đơn hàng thành công!"
                      );

                      if (paymentMethod === "online") {
                        const url =
                          responseData.data?.vtUrl ||
                          responseData.data?.vt?.data?.url;
                        if (url) {
                          openEmbeddedUrl(url, "Thanh toán");
                        } else {
                          message.warning("Không tìm thấy link thanh toán");
                        }
                      } else {
                        setCart({});
                        setStep("cart");
                        setPhoneNumber("");
                        setPaymentMethod("cash");
                      }
                    } else {
                      message.error(
                        responseData?.message || "Tạo đơn hàng thất bại"
                      );
                    }
                  } catch (error: any) {
                    console.error(error);
                    message.error(
                      await parseErrorMessage(error?.response?.data)
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                Xác nhận
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          padding: "16px 24px",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        <Space size={16} align="start">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            size="large"
            style={{ padding: "0 8px 0 0", marginTop: 2 }}
          />
          <div>
            <Title level={4} style={{ margin: 0, lineHeight: 1.4 }}>
              {data.nameEvent}
            </Title>
            <Space size={24} style={{ marginTop: 8 }} wrap>
              <Space>
                <CalendarOutlined style={{ color: "#8c8c8c" }} />
                <Text type="secondary">
                  {dayjs(data.startDateEvent).format("DD/MM/YYYY HH:mm")}
                </Text>
              </Space>
              <Space>
                <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                <Text type="secondary">{data.stadium?.name || "-"}</Text>
              </Space>
            </Space>
          </div>
        </Space>
      </div>

      {/* Main Content */}
      <div style={{ padding: "24px", flex: 1, paddingBottom: 100 }}>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} lg={16} xl={14}>
            <Card
              bordered={false}
              bodyStyle={{ padding: "24px" }}
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {data.stadium?.name && (
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    {data.stadium.name}
                  </Title>
                  {data.stadium.image ? (
                    <img
                      src={data.stadium.image}
                      alt="Stadium Map"
                      style={{ maxWidth: "100%", borderRadius: 8 }}
                    />
                  ) : null}
                </div>
              )}

              <Title level={5} style={{ marginBottom: 16 }}>
                Danh sách vé
              </Title>
              {tickets.length > 0 ? (
                <div>{tickets.map(renderTicket)}</div>
              ) : (
                <Empty description="Chưa có vé nào cho sự kiện này" />
              )}
            </Card>
          </Col>
        </Row>
      </div>

      {/* Cart Summary Sticky Footer */}
      {totalQty > 0 && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#fff",
            padding: "16px 24px",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
            borderTop: "1px solid #f0f0f0",
            zIndex: 10,
          }}
        >
          <Row justify="center">
            <Col xs={24} lg={16} xl={14}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text type="secondary" style={{ fontSize: 16 }}>
                    Tổng cộng ({totalQty} vé):{" "}
                  </Text>
                  <Text
                    strong
                    style={{ fontSize: 24, color: "#e2111c", marginLeft: 8 }}
                  >
                    {totalAmount.toLocaleString("vi-VN")}đ
                  </Text>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    style={{
                      backgroundColor: "#025433",
                      borderColor: "#025433",
                      borderRadius: 8,
                      padding: "0 32px",
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      setStep("checkout");
                    }}
                  >
                    Tiếp theo
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default TicketDetailCtrl;
